/**
 * Orval-facing mutator. All generated React Query hooks pipe their config
 * through this single `fetch`-backed function so we have one place to
 * inject the Ping-issued Authorization header, unwrap the response
 * envelope, and surface error metadata.
 *
 * Auth is bearer-token: `getAccessToken()` acquires a Ping OIDC access
 * token from the `oidc-client-ts` `UserManager` set up in `main.tsx` (via
 * `initOidc`) and the value is forwarded as `Authorization: Bearer <token>`.
 * We do NOT forward cookies — the API does not consume a session cookie
 * under the post-migration architecture.
 */

import { trackException } from '@/lib/app-insights';
import { pushFetch } from '@/lib/breadcrumbs';
import { scrubUrl } from '@/lib/url-scrub';
import {
  isAnonymousOperation,
  operationResponseSchemas,
  resolveOperationId,
} from '@cotality/marketplace-contracts/operation-registry';
import { toast } from 'sonner';
import { getAccessToken, getAccessTokenIfSignedIn } from './auth-token';

/**
 * Classify a `fetch` rejection so SREs can pivot AI exception rows by failure
 * mode. The Fetch spec is famously vague about which TypeError you get for
 * which network condition, so we lean on the message-shape heuristics that
 * Chromium / Firefox / Safari all happen to produce today.
 *
 * - `abort`     — caller-driven `AbortController.abort()` that surfaces as an
 *                 `AbortError`-named Error rather than via `signal.aborted`.
 *                 Kept distinct from the silent-skip path so the classifier
 *                 stays accurate when invoked in adjacent code.
 * - `network`   — DNS failure, TCP reset, offline, plain unreachable host
 *                 (`TypeError: Failed to fetch` / `NetworkError`).
 * - `cors`      — preflight or response CORS denial.
 * - `unknown`   — everything else; kept so the property is never null in AI.
 */
function classifyNetworkError(err: unknown): 'abort' | 'network' | 'cors' | 'unknown' {
  if (err instanceof Error) {
    if (err.name === 'AbortError') return 'abort';
    const msg = err.message.toLowerCase();
    if (msg.includes('cors')) return 'cors';
    if (msg.includes('failed to fetch') || msg.includes('networkerror')) return 'network';
  }
  return 'unknown';
}

export interface MutatorConfig {
  url: string;
  method: string;
  data?: unknown;
  // `| undefined` is load-bearing under `exactOptionalPropertyTypes: true`.
  // Orval emits `{ params: T | undefined }` literally for operations with
  // all-optional query params (auditList, pluginsList, submissionsList,
  // teamsList, usersList, sourcesSyncJobs, etc.), so the property is
  // *present* on the call site even when undefined. Without the explicit
  // `| undefined`, those eleven callers fail TS2379. Runtime is unaffected:
  // the `if (config.params)` guard below already short-circuits undefined.
  params?: Record<string, unknown> | undefined;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  readonly code: string | undefined;
  readonly status: number;
  readonly details: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface EnvelopeErrorBody {
  success: false;
  error: { code: string; message: string; details?: unknown };
}

interface EnvelopeSuccessBody<T> {
  success: true;
  data: T;
}

type EnvelopeBody<T> = EnvelopeSuccessBody<T> | EnvelopeErrorBody | Partial<EnvelopeErrorBody>;

/**
 * Type-level unwrap of orval's envelope-shaped wire type to the inner
 * `data` payload. Orval emits operation return types as
 * `{ success: boolean; data: T }` because that is what the OpenAPI spec
 * documents. The mutator strips the envelope at runtime; this conditional
 * type does the matching strip at compile time so callers (and the orval-
 * generated `Awaited<ReturnType<typeof submissionsList>>` projections in
 * `generated.ts`) see `T`, not `{ data: T; success: boolean }`. Operations
 * that opt out of the envelope (raw payloads — manifest) have no `data`
 * key and pass through unchanged.
 */
export type UnwrapEnvelope<T> = T extends { data: infer U; success: boolean } ? U : T;

export async function mutator<TWire>(config: MutatorConfig): Promise<UnwrapEnvelope<TWire>> {
  const url = new URL(config.url, window.location.origin);
  if (config.params) {
    for (const [key, value] of Object.entries(config.params)) {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  // Acquire a Ping access token, branching on the endpoint's declared
  // anonymity (the contract's `anonymous` flag, resolved via
  // `isAnonymousOperation`). Anonymous endpoints (e.g. `/api/status`)
  // proceed token-less when nobody is signed in — `getAccessTokenIfSignedIn`
  // returns `null` rather than forcing an interactive redirect. Every other
  // endpoint still requires a token via the throwing `getAccessToken()`
  // (silent renew if the cached user is expired, interactive redirect if
  // silent renew fails — see `auth-token.ts`). The header is the sole
  // signal the API uses to identify the caller; `credentials` is
  // intentionally left unset so we never send cookies.
  const anonymous = isAnonymousOperation(config.method, url.pathname);
  const accessToken = anonymous ? await getAccessTokenIfSignedIn() : await getAccessToken();

  // Only emit `content-type: application/json` when a body is actually
  // serialised. Fastify's strict JSON parser rejects requests carrying
  // the header but no body with `FST_ERR_CTP_EMPTY_JSON_BODY` —
  // bodyless POSTs (e.g. `/requeue`, `/unyank`, `/deprecate`) tripped
  // this until the conditional below was added. Caller-supplied
  // headers always win so an explicit override still flows through.
  //
  // Use a `Headers` instance so header names are case-normalised. Orval
  // emits `Content-Type` (capital) on every body-carrying operation; a
  // naive plain-object check on `'content-type'` would miss it and let
  // the duplicate survive into `fetch`, which joins same-named headers
  // with `, ` and trips Fastify's content-type parser with 415.
  const headers = new Headers({ ...(config.headers ?? {}) });
  if (accessToken && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${accessToken}`);
  }
  if (config.data !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const init: RequestInit = {
    method: config.method,
    headers,
  };
  if (config.data !== undefined) {
    init.body = JSON.stringify(config.data);
  }
  if (config.signal) {
    init.signal = config.signal;
  }

  const startMs = performance.now();
  const scrubbedUrl = scrubUrl(url);
  let res: Response;
  try {
    res = await fetch(url.toString(), init);
  } catch (err) {
    // Caller-driven cancellation: silent. No telemetry, no breadcrumb —
    // `AbortController.abort()` is a flow-control signal, not an error worth
    // paging AI for. (React Query's `cancelQueries`, route teardown, and
    // unmount-during-flight all funnel through here.)
    if (init.signal?.aborted) {
      throw err;
    }
    // Network rejection (DNS, TCP reset, offline, CORS preflight). The
    // breadcrumb status is `0` — the canonical "no response was received"
    // signal SREs filter on. Order matters: push the breadcrumb FIRST so
    // `trackException`'s snapshot includes it (T7 + T3 ordering invariant).
    pushFetch(scrubbedUrl, 0, performance.now() - startMs);
    trackException(err instanceof Error ? err : new Error(String(err)), {
      source: 'api-network',
      url: scrubbedUrl,
      method: config.method,
      reason: classifyNetworkError(err),
    });
    throw err;
  }

  // 204 No Content carries no body by HTTP spec, so `res.json()` on the
  // empty stream throws a SyntaxError — which the `body === null` guard
  // below would misclassify as a MALFORMED_RESPONSE 500. Every bodyless
  // success the API emits (DELETE /api/teams/:id, DELETE
  // /api/teams/:id/admins/:userId, DELETE /api/flags/:key) returns 204;
  // short-circuit it as a successful `undefined` (void) payload. Without
  // this, a successful delete surfaced as an error toast, the confirm
  // dialog stayed open, and the operator retried into a 404 on the
  // already-soft-deleted row. 204 is the only no-body status the API
  // returns (the manifest 304 path is conditional-request-only and never
  // routes through this mutator).
  if (res.status === 204) {
    pushFetch(scrubbedUrl, res.status, performance.now() - startMs);
    return undefined as UnwrapEnvelope<TWire>;
  }

  // Capture the underlying SyntaxError from `res.json()` so the
  // MALFORMED_RESPONSE branch below can forward the message into AI
  // properties. Distinguishing HTML-error-page from truncated-chunked from
  // gzip-mismatch is impossible without the parser's own error text.
  //
  // TS narrowing caveat: a `let` variable assigned inside a `.catch()` arrow
  // is narrowed to its initialiser value after the await resolves (closures
  // bust control-flow analysis). Boxing the reference in a single-property
  // object dodges the narrowing — TS treats property reads as widened to the
  // declared type.
  const parseErrorRef: { value: SyntaxError | null } = { value: null };
  const body = (await res.json().catch((e: unknown) => {
    parseErrorRef.value = e instanceof SyntaxError ? e : new SyntaxError(String(e));
    return null;
  })) as EnvelopeBody<UnwrapEnvelope<TWire>> | null;

  // Breadcrumb every response — success and error alike. This MUST run
  // before the error throw below so the breadcrumb buffer is populated
  // when `trackException` snapshots it (T7 + T3 ordering invariant).
  pushFetch(scrubbedUrl, res.status, performance.now() - startMs);

  if (!res.ok || (body && body.success === false)) {
    const err = body?.success === false ? body.error : undefined;
    const apiError = new ApiError(
      err?.message ?? 'Request failed',
      res.status,
      err?.code,
      err?.details,
    );
    trackException(apiError, {
      source: 'api-http',
      url: scrubbedUrl,
      status: res.status,
      code: err?.code,
    });
    throw apiError;
  }

  // 2xx with an unparseable body is a server bug — the success path
  // expects either the envelope or a raw-payload object. Surface it as
  // an `ApiError` so caller toasts and React Query error boundaries fire
  // the same way they do for any other transport-layer failure.
  if (body === null) {
    const apiError = new ApiError(
      'Server response was not valid JSON',
      500,
      'MALFORMED_RESPONSE',
      {},
    );
    trackException(apiError, {
      source: 'api-http',
      url: scrubbedUrl,
      status: res.status,
      code: 'MALFORMED_RESPONSE',
      parseError: parseErrorRef.value?.message,
    });
    throw apiError;
  }

  let payload: UnwrapEnvelope<TWire>;
  if (body.success === true) {
    payload = body.data as UnwrapEnvelope<TWire>;
  } else {
    // Endpoints that return the raw payload (e.g. `/api/.well-known/marketplace.json`)
    // bypass the envelope; treat the decoded body as the data.
    payload = body as unknown as UnwrapEnvelope<TWire>; // allow-as-unknown: raw-payload endpoints (manifest) bypass the envelope
  }

  // Dev-only contract guard (T11 of orval rewire). Re-parse the response
  // payload against the Zod schema registered for this operation; a
  // mismatch surfaces as a console.warn + sonner toast — never a thrown
  // error, because the network layer must not break dev UX when the server
  // drifts ahead of the contract. The MODE check tree-shakes the `devReparse`
  // call (and `sonner`) out of the prod bundle. Note `isAnonymousOperation`
  // runs on the hot path above (prod + dev), so the `operation-registry`
  // module — and the `routeRegistry` it compiles, whose entries reference the
  // contract Zod schemas — stays in the prod bundle regardless; the MODE guard
  // does NOT keep the schema graph out (`isAnonymousOperation` alone pins it).
  if (import.meta.env.MODE !== 'production') {
    devReparse(url.pathname, config.method, payload);
  }

  return payload;
}

/**
 * Run the dev-only Zod re-parse for a single response payload. Pulled out
 * of the main flow so the `MODE !== 'production'` branch in `mutator()` is
 * a single unconditional call — Vite/Rollup elides this entire function
 * (and its module-level imports) in prod builds via standard dead-code
 * elimination.
 *
 * Unknown operations are SILENT. Drift between openapi.json and registry.ts
 * is caught by `pnpm contracts:audit` and the T5 drift gate — re-warning
 * here would create paging noise without adding signal.
 */
function devReparse(pathname: string, method: string, payload: unknown): void {
  const operationId = resolveOperationId(method, pathname);
  if (!operationId) return;
  const schema = operationResponseSchemas[operationId];
  if (!schema) return;
  const result = schema.safeParse(payload);
  if (result.success) return;
  const issueSummary = result.error.issues
    .slice(0, 3)
    .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
    .join('; ');
  // eslint-disable-next-line no-console
  console.warn(
    `[mutator] response for ${operationId} (${method} ${pathname}) failed contract re-parse: ${issueSummary}`,
  );
  toast.error(`Contract drift: ${operationId}`, {
    description: issueSummary,
  });
}
